import { THEME } from '../../../../web/components-v2/Theme/theme';

export const threadTimelineStyles = {
	thread: {
		marginBottom: 8,
		padding: '12px 18px',
		borderRadius: THEME.common.borderRadius,
		border: `1px solid ${THEME.colors.border.main}`,
	},
	threadCustomer: {
		backgroundColor: THEME.colors.background.light,
	},
	threadAgent: {
		backgroundColor: THEME.colors.primary.light,
		borderColor: THEME.colors.border.main,
	},
	threadNote: {
		backgroundColor: THEME.colors.info.light,
		borderColor: THEME.colors.info.main,
	},
	threadAiDraft: {
		backgroundColor: THEME.colors.secondary.light,
		borderColor: THEME.colors.secondary.main,
	},
	threadSystem: {
		backgroundColor: THEME.colors.background.main,
		borderColor: THEME.colors.border.main,
		// Activity rows (category change, AI draft, etc.) are compact metadata, not
		// messages — tighten their padding and gap so a run of them doesn't sprawl.
		marginBottom: 8,
		padding: '6px 16px',
		textAlign: 'center' as const,
		fontSize: 13,
		color: THEME.colors.text.light,
	},
	/** Emphasis for the actor name within an activity event row. */
	eventActor: { fontWeight: THEME.typography.fontWeights.bold, color: THEME.colors.text.main },
	/** Muted timestamp suffix on an activity event row. */
	eventTime: { color: THEME.colors.text.light },
	threadRejected: { opacity: 0.5 },
	threadAvatarAiDraft: { backgroundColor: THEME.colors.secondary.dark, color: THEME.colors.secondary.contrastText },
	draftActions: {
		display: 'flex',
		gap: 8,
		marginTop: 12,
		paddingTop: 12,
		borderTop: `1px solid ${THEME.colors.border.main}`,
	},
	draftAccepted: {
		color: THEME.colors.success.dark,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	draftRejectedLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	collapsedToggle: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.secondary.dark,
		cursor: 'pointer',
		padding: '4px 0',
		':hover': { textDecoration: 'underline' },
	},
	threadHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 8,
	},
	threadAuthorRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	threadAvatar: {
		width: 32,
		height: 32,
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	threadAvatarCustomer: {
		backgroundColor: THEME.colors.border.main,
		color: THEME.colors.text.light,
	},
	threadAvatarNote: {
		backgroundColor: THEME.colors.warning.main,
		color: THEME.colors.warning.contrastText,
	},
	threadAuthorName: { fontSize: THEME.typography.fontSizes.sm, fontWeight: THEME.typography.fontWeights.bold },
	threadAuthorEmail: { fontSize: THEME.typography.fontSizes.xs, color: THEME.colors.text.light },
	threadRecipients: { fontSize: THEME.typography.fontSizes.xs, color: THEME.colors.text.light },
	threadTime: { fontSize: THEME.typography.fontSizes.xs, color: THEME.colors.text.light },
	threadBody: {
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: '1.5',
		color: THEME.colors.text.main,
		// Imported plain-text emails carry long unbroken URLs that would spill past the bubble.
		overflowWrap: 'anywhere' as const,
	},
	noteBadge: {
		display: 'inline-block',
		fontSize: THEME.typography.fontSizes.xxs,
		backgroundColor: THEME.colors.warning.main,
		color: THEME.colors.warning.contrastText,
		padding: '2px 8px',
		borderRadius: 3,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	deliveryFailedBadge: {
		display: 'inline-block',
		fontSize: THEME.typography.fontSizes.xxs,
		backgroundColor: THEME.colors.danger.main,
		color: THEME.colors.danger.contrastText,
		padding: '2px 8px',
		borderRadius: 3,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	loading: { padding: 24, color: THEME.colors.text.light },
	error: { padding: 24, color: THEME.colors.danger.dark },
	aiDraftBadge: {
		display: 'inline-block',
		fontSize: THEME.typography.fontSizes.xxs,
		backgroundColor: THEME.colors.secondary.main,
		color: THEME.colors.secondary.contrastText,
		padding: '2px 8px',
		borderRadius: 3,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	threadSystemClickable: {
		cursor: 'pointer',
		color: THEME.colors.secondary.main,
	},
	aiScoreBadgeInline: {
		marginLeft: 8,
		cursor: 'help',
	},
	threadBodyPreWrap: {
		whiteSpace: 'pre-wrap' as const,
	},
	showFullToggle: {
		display: 'inline-block',
		background: 'none',
		border: 'none',
		padding: '4px 0',
		marginTop: 8,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.primary.main,
		cursor: 'pointer',
		':hover': { textDecoration: 'underline' },
	},
	threadAttachments: {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: 8,
		marginTop: 12,
		paddingTop: 10,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	attachmentChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 10px',
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
		textDecoration: 'none',
		cursor: 'pointer',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
			borderColor: THEME.colors.primary.main,
		},
	},
	attachmentIcon: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	attachmentName: {
		maxWidth: 180,
		overflow: 'hidden' as const,
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	attachmentSize: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		flexShrink: 0,
	},
	attachmentHoverPreview: {
		padding: THEME.spacing.xs,
		pointerEvents: 'none' as const,
	},
	attachmentHoverPreviewImage: {
		display: 'block',
		maxWidth: THEME.breakpoints.xs,
		maxHeight: THEME.breakpoints.xs / 2,
		borderRadius: THEME.common.borderRadius,
		objectFit: 'contain' as const,
	},
} as const;
